import * as THREE from 'three';
import { Kit, place } from '../seasons/kit.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/**
 * The clan of the Horn's stone boars (El Cuerno 1, docs/SAGAS.md): a heavy boar carved out of the titan's own grey-blue
 * stone, faceted like a chisel left it, moss on the back and lichen on the flanks, a crest of stone bristles down the
 * spine, pale tusks curling up and the clan's red daubed across the shoulder. The body is one vertex-coloured geometry
 * (no legs), the leg another, so a whole herd is two instanced draw calls; `seed` varies the stone and the moss.
 *
 * Faces +Z, feet at y = 0; about 1.0 long and 0.62 tall at the crest before scaling.
 */

/** Hip and shoulder joints (legs hang from here), front and back, either side. */
export const BOAR_LEGS: readonly [number, number][] = [
  [-0.16, 0.2],
  [0.16, 0.2],
  [-0.16, -0.22],
  [0.16, -0.22],
];
export const BOAR_HIP_Y = 0.24;

const STONE = ['#7c8794', '#727d8a', '#86909b', '#6d7682'];
const DARK = ['#59626e', '#525a66'];
const MOSS = ['#4f6a3c', '#5a7444', '#476036'];
const LICHEN = ['#a9b89c', '#9fb2a4', '#b4c0aa'];
const TUSK = '#e4e2d8';
const CLAN = '#a43a2c';

const pick = <T>(a: readonly T[], n: number): T => a[((n % a.length) + a.length) % a.length] as T;

/** The boar's body (head, tusks, crest, moss), legs apart. */
export const boarBody = (seed = 0): THREE.BufferGeometry => {
  const k = new Kit();
  const id = new THREE.Matrix4();
  const stone = pick(STONE, seed);
  const dark = pick(DARK, seed + 1);
  const moss = pick(MOSS, seed * 2 + 1);
  const lichen = pick(LICHEN, seed + 2);
  const v = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
  // The barrel of the body: a faceted lump, high at the shoulder, falling to the rump.
  const body = new THREE.IcosahedronGeometry(0.5, 1);
  k.add(body, stone, id, place(0, 0.42, 0.0, 0, v(0.5, 0.36, 0.7)));
  // The hump over the shoulders, heavier, and the haunches.
  k.add(body, stone, id, place(0, 0.5, 0.12, 0, v(0.48, 0.3, 0.38)));
  k.add(body, dark, id, place(0, 0.39, -0.2, 0, v(0.44, 0.3, 0.32)));
  // The head: a wedge down and forward, the snout a flat disc, the jaw darker.
  const wedge = new THREE.CylinderGeometry(0.1, 0.17, 0.32, 5);
  k.add(wedge, stone, id, place(0, 0.38, 0.43, 0, 1, Math.PI / 2 + 0.45));
  k.add(new THREE.CylinderGeometry(0.075, 0.08, 0.05, 7), dark, id, place(0, 0.29, 0.6, 0, 1, Math.PI / 2 + 0.45));
  for (const s of [-1, 1]) k.add(new THREE.SphereGeometry(0.012, 4, 3), '#1c1e22', id, place(s * 0.028, 0.305, 0.628));
  k.add(new THREE.BoxGeometry(0.15, 0.06, 0.2), dark, id, place(0, 0.27, 0.46, 0, 1, 0.35));
  // Tusks: pale, curling up and out from the jaw.
  for (const s of [-1, 1]) {
    k.add(new THREE.ConeGeometry(0.022, 0.14, 5), TUSK, id, place(s * 0.07, 0.31, 0.56, 0, 1, -0.25, -s * 0.6));
    k.add(new THREE.ConeGeometry(0.014, 0.06, 4), TUSK, id, place(s * 0.1, 0.37, 0.57, 0, 1, -0.7, -s * 0.3));
  }
  // Eyes: dark carved slits, a brow over each.
  for (const s of [-1, 1]) {
    k.add(new THREE.BoxGeometry(0.035, 0.014, 0.02), '#15171b', id, place(s * 0.085, 0.43, 0.47, s * 0.35));
    k.add(new THREE.BoxGeometry(0.06, 0.03, 0.04), dark, id, place(s * 0.085, 0.455, 0.46, s * 0.35, 1, 0.2));
    // Ears, pointed and laid back.
    k.add(new THREE.ConeGeometry(0.045, 0.12, 4), stone, id, place(s * 0.09, 0.53, 0.33, s * 0.3, 1, -0.9, -s * 0.35));
  }
  // The crest: a row of stone bristles down the spine, tallest at the shoulder.
  for (let i = 0; i < 7; i++) {
    const t = i / 6;
    const h = 0.14 - Math.abs(t - 0.3) * 0.12;
    k.add(new THREE.ConeGeometry(0.035, h, 4), dark, id, place(0, 0.64 - t * 0.08 + h * 0.3, 0.26 - t * 0.5, i * 0.6, 1, -0.35));
  }
  // Moss over the back, lichen spots on the flanks.
  const blob = new THREE.IcosahedronGeometry(0.5, 0);
  for (let i = 0; i < 4; i++) k.add(blob, moss, id, place(((seed + i) % 3) * 0.05 - 0.05, 0.64 - i * 0.025, 0.05 - i * 0.1, i, v(0.16, 0.05, 0.13)));
  for (let i = 0; i < 4; i++) {
    const s = i % 2 ? 1 : -1;
    k.add(new THREE.CircleGeometry(0.035 + ((seed + i) % 3) * 0.01, 6), lichen, id, place(s * 0.252, 0.4 + (i % 3) * 0.04, -0.16 + i * 0.07, s * Math.PI / 2));
  }
  // The clan's red, daubed across the shoulder: two strokes either side.
  for (const s of [-1, 1]) for (const dy of [0, 0.06]) k.add(new THREE.BoxGeometry(0.03, 0.032, 0.17), CLAN, id, place(s * 0.228, 0.5 - dy, 0.12, 0, 1, 0, -0.35 * s));
  // A stub of a tail, curled.
  k.add(new THREE.TorusGeometry(0.035, 0.014, 4, 8, Math.PI * 1.4), dark, id, place(0, 0.46, -0.36, 0, 1, 0.4));
  const mesh = k.build(new THREE.MeshBasicMaterial(), 'boarBody');
  return (mesh as THREE.Mesh).geometry;
};

/** A leg: a short stone stump hanging from its hip at y = 0, a split hoof at the bottom. */
export const boarLeg = (): THREE.BufferGeometry => {
  const k = new Kit();
  const m = new THREE.Matrix4();
  k.add(new THREE.CylinderGeometry(0.06, 0.045, BOAR_HIP_Y - 0.04, 5).translate(0, -(BOAR_HIP_Y - 0.04) / 2, 0), '#6a7480', m);
  k.add(new THREE.CylinderGeometry(0.05, 0.055, 0.05, 5).translate(0, -BOAR_HIP_Y + 0.025, 0.01), '#3e444c', m);
  const mesh = k.build(new THREE.MeshBasicMaterial(), 'boarLeg');
  return (mesh as THREE.Mesh).geometry;
};

/** A stone boar standing (the gallery, and the bastion's corral). */
export const stoneBoar = (o?: BaseOpts & { seed?: number }): Model => {
  const c = begin(o, 0.9, 79);
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true });
  c.g.add(new THREE.Mesh(boarBody(o?.seed ?? 0), mat));
  const leg = boarLeg();
  for (const [x, z] of BOAR_LEGS) {
    const l = new THREE.Mesh(leg, mat);
    l.position.set(x, BOAR_HIP_Y, z);
    c.g.add(l);
  }
  return done(c, 0.7);
};
